﻿import React, { useState, useEffect, useRef, Suspense } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid, TransformControls } from '@react-three/drei';
import { useCadStore } from './store/cadStore';
import { geometryEngine } from './services/geometryEngine';
import { slicerService } from './services/slicerService';
import { useThemeTokens } from './theme';
import { getSession, logout } from './store/auth';
import PinLock from './components/PinLock';
import LoginScreen from './components/LoginScreen';
import Sketch2D from './components/Sketch2D';
import SettingsModal from './components/SettingsModal';
import AICommandBar from './components/AICommandBar';
import BooleanToolbar from './components/BooleanToolbar';
import ShapeLibraryPanel from './components/ShapeLibraryPanel';
import LODMesh from './components/LODMesh';
import { recordMutation, amplify } from './services/learningService';
import {
    Search, Settings, Box, Layers, Download, Cpu, Circle, Cylinder,
    Move, RotateCcw, Maximize, X, Printer, Copy, Undo2, Redo2, Trash2,
    AlertTriangle, Smartphone, Lock, Crosshair, Sun,
    Triangle, Spline, Hexagon, CircleDot, Disc, Pill, Gem, Group, Diamond,
    Eye, EyeOff, FolderOpen, FilePlus, Clipboard, ClipboardPaste,
    Save, MessageSquare, Keyboard, Ruler, LogOut, Pencil, Eraser, Square as SquareIcon, Type as TypeIcon,
    MousePointer2, Grid3x3, Magnet, Focus,
} from 'lucide-react';

/* ===== ERROR BOUNDARIES ===== */
class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null }; }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) { console.error('[RooCAD ErrorBoundary]', error, info); }
    render() {
        if (this.state.error) {
            return (
                <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#e2e8f0', fontFamily: 'sans-serif', padding: 24 }}>
                    <AlertTriangle size={48} color="#f59e0b" />
                    <h1 style={{ marginTop: 16, fontSize: 20, fontWeight: 700 }}>RooCAD hit an error</h1>
                    <p style={{ marginTop: 8, color: '#94a3b8', fontSize: 13, maxWidth: 480, textAlign: 'center' }}>{String(this.state.error?.message || this.state.error)}</p>
                    <button onClick={() => this.setState({ error: null })} style={{ marginTop: 20, padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Restart RooCAD</button>
                </div>
            );
        }
        return this.props.children;
    }
}

class CanvasBoundary extends React.Component {
    constructor(props) { super(props); this.state = { failed: false }; }
    static getDerivedStateFromError() { return { failed: true }; }
    componentDidCatch(err) { console.error('[RooCAD Canvas]', err); }
    render() {
        if (this.state.failed) {
            return (
                <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0b1220', color: '#64748b', fontFamily: 'sans-serif', gap: 12 }}>
                    <Box size={40} />
                    <p style={{ fontSize: 13 }}>3D viewport unavailable (WebGL may be disabled)</p>
                    <button onClick={() => this.setState({ failed: false })} style={{ padding: '8px 16px', background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>Retry</button>
                </div>
            );
        }
        return this.props.children;
    }
}

/* ===== DEVICE DETECTION â€” three independent UI trees ===== */
function useDeviceType() {
    const [device, setDevice] = useState(() => detect());
    useEffect(() => {
        const fn = () => setDevice(detect());
        window.addEventListener('resize', fn);
        window.addEventListener('orientationchange', fn);
        return () => { window.removeEventListener('resize', fn); window.removeEventListener('orientationchange', fn); };
    }, []);
    return device;
}
function detect() {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    if (w < 640) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
}

/* ===== 3D SCENE ===== */
function PrimitiveGeometry({ type }) {
    switch (type) {
        case 'sphere': case 'sphere_small': return <sphereGeometry args={type === 'sphere' ? [0.5, 32, 32] : [0.2, 24, 24]} />;
        case 'cylinder': case 'tube': return <cylinderGeometry args={type === 'tube' ? [0.3, 0.3, 1, 24] : [0.5, 0.5, 1, 32]} />;
        case 'cylinder_thin': return <cylinderGeometry args={[0.1, 0.1, 1, 16]} />;
        case 'torus': return <torusGeometry args={[0.5, 0.2, 16, 64]} />;
        case 'torus_knot': return <torusKnotGeometry args={[0.4, 0.12, 100, 16]} />;
        case 'cone': case 'pyramid': return <coneGeometry args={type === 'pyramid' ? [0.6, 1, 4] : [0.5, 1, 32]} />;
        case 'wedge': return <cylinderGeometry args={[0.01, 0.5, 1, 3]} />;
        case 'prism': return <cylinderGeometry args={[0.5, 0.5, 1, 6]} />;
        case 'diamond': return <octahedronGeometry args={[0.6]} />;
        case 'gem': return <dodecahedronGeometry args={[0.55]} />;
        case 'tetra': return <tetrahedronGeometry args={[0.7]} />;
        case 'capsule': return <capsuleGeometry args={[0.35, 0.5, 8, 16]} />;
        default: return <boxGeometry args={[1, 1, 1]} />;
    }
}

const GROUP_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

function PrimitiveMesh({ part, transformMode = 'translate' }) {
    const selected = useCadStore((s) => s.selectedPartId === part.id);
    const setSelected = useCadStore((s) => s.setSelectedPart);
    const groupColor = part.group ? GROUP_COLORS[[...part.group].reduce((a, c) => a + c.charCodeAt(0), 0) % GROUP_COLORS.length] : null;
    const mesh = (
        <mesh
            position={part.position}
            scale={part.scale}
            rotation={part.rotation || [0, 0, 0]}
            castShadow
            receiveShadow
            visible={!part.hidden}
            onPointerDown={(e) => { e.stopPropagation(); setSelected(part.id); }}
        >
            <PrimitiveGeometry type={part.type} />
            <meshStandardMaterial
                color={part.color || groupColor || '#3b82f6'}
                roughness={0.35}
                metalness={0.15}
                transparent={!!part.group}
                opacity={part.group ? 0.92 : 1}
                emissive={selected ? '#1d4ed8' : '#000000'}
                emissiveIntensity={selected ? 0.45 : 0}
            />
        </mesh>
    );
    /* wrap in TransformControls when selected so the gizmo actually drives the mesh */
    if (selected && transformMode !== 'select') {
        return (
            <TransformControls mode={transformMode} size={0.8}>
                {mesh}
            </TransformControls>
        );
    }
    return mesh;
}

function SceneContent({ transformMode }) {
    const currentModel = useCadStore((s) => s.currentModel);
    const selectedPartId = useCadStore((s) => s.selectedPartId);
    const sceneRef = useRef();
    useEffect(() => {
        if (sceneRef.current) window.__roocad_scene = sceneRef.current;
    }, [currentModel]);
    return (
        <group ref={sceneRef}>
            {currentModel.map((part) => (
                /* Selected (and not in pure-select mode: full gizmo; the rest render
                   through dynamic LOD so the canvas stays at 60 FPS. */
                selectedPartId === part.id && transformMode !== 'select'
                    ? <PrimitiveMesh key={part.id} part={part} transformMode={transformMode} />
                    : <LODMesh key={part.id} part={part} />
            ))}
        </group>
    );
}

/* Renders a bounded build volume cage, a finite floor grid and a ground plane.
   Everything the user models must live inside this volume — the world is no
   longer an infinite "open world" where parts can get lost. */
function BuildVolume({ pb, t, gridVisible, showCage }) {
    const W = pb.x || 220, D = pb.y || 220, H = pb.z || 250;
    const cageSize = Math.max(W, D, H);
    const cellSize = Math.max(1, Math.round(cageSize / 18));
    return (
        <>
            {/* finite, non-infinite grid covering the build footprint */}
            {gridVisible && (
                <Grid
                    position={[0, 0, 0]}
                    args={[W, D]}
                    cellSize={cellSize}
                    sectionSize={cellSize * 5}
                    cellColor={t.grid}
                    sectionColor={t.gridSection}
                    infiniteGrid={false}
                    fadeDistance={cageSize * 4}
                />
            )}
            {/* grounding floor plane — click to deselect */}
            <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}
                onPointerDown={(e) => { e.stopPropagation(); useCadStore.getState().setSelectedPart(null); }}>
                <planeGeometry args={[W, D]} />
                <meshStandardMaterial color={t.viewportBg} transparent opacity={0.55} />
            </mesh>
            {/* build volume wireframe cage */}
            {showCage && (
                <>
                    <lineSegments>
                        <edgesGeometry args={[new THREE.BoxGeometry(W, H, D)]} />
                        <lineBasicMaterial color={t.accent} transparent opacity={0.85} />
                    </lineSegments>
                    <lineSegments>
                        <edgesGeometry args={[new THREE.BoxGeometry(W, 0.01, D)]} />
                        <lineBasicMaterial color={t.gridSection} />
                    </lineSegments>
                </>
            )}
        </>
    );
}

function CADViewer({ onContextMenu }) {
    const [transformMode, setTransformMode] = useState('translate');
    const t = useThemeTokens();
    const gridVisible = useCadStore((s) => s.settings.gridVisible);
    const showBuildVolume = useCadStore((s) => s.settings.showPrintBed);
    const pb = useCadStore((s) => s.settings.printBed) || { x: 220, y: 220, z: 250 };
    const W = pb.x || 220, D = pb.y || 220, H = pb.z || 250;
    const radius = Math.max(W, D, H);
    const controlsRef = useRef();
    // expose controls + camera for fit-view / reset from anywhere (incl. AI panel)
    useEffect(() => {
        const controls = controlsRef.current;
        if (!controls) return;
        window.__roocad_controls = controls;
        const frame = () => {
            // frame the current model if there is one, otherwise frame the build volume
            const model = useCadStore.getState().currentModel || [];
            let cx = 0, cy = H / 2, cz = 0, span = radius;
            if (model.length) {
                let min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
                model.forEach((p) => {
                    const [bw, bh, bd] = geometryEngine.baseBounds(p.type);
                    const pos = p.position, scl = p.scale || [1, 1, 1];
                    const half = [Math.abs(bw * scl[0]) / 2, Math.abs(bh * scl[1]) / 2, Math.abs(bd * scl[2]) / 2];
                    for (let i = 0; i < 3; i++) {
                        min[i] = Math.min(min[i], pos[i] - half[i]);
                        max[i] = Math.max(max[i], pos[i] + half[i]);
                    }
                });
                cx = (min[0] + max[0]) / 2; cy = (min[1] + max[1]) / 2; cz = (min[2] + max[2]) / 2;
                span = Math.max(max[0] - min[0], max[1] - min[1], max[2] - min[2], 1);
            }
            const target = new THREE.Vector3(cx, cy, cz);
            controls.target.copy(target);
            const d = Math.min(Math.max(span * 1.6, radius * 0.35), radius * 2.2);
            controls.object.position.set(target.x + d * 0.75, target.y + d * 0.62, target.z + d * 0.75);
            controls.update();
        };
        window.__roocad_fitView = frame;
        frame();
        return () => { window.__roocad_controls = null; window.__roocad_fitView = null; };
    }, [W, D, H, radius]);
    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', background: t.viewportBg }}
            onContextMenu={onContextMenu}>
            <CanvasBoundary>
                <Canvas shadows camera={{ position: [14, 10, 14], fov: 50 }} dpr={[1, 2]}>
                    <color attach="background" args={[t.viewportBg]} />
                    <fog attach="fog" args={[t.fog, radius * 3, radius * 6]} />
                    <ambientLight intensity={0.5} />
                    <directionalLight position={[10, 15, 10]} intensity={1.2} castShadow shadow-mapSize={[1024, 1024]} />
                    <directionalLight position={[-10, 5, -10]} intensity={0.3} />
                    <Suspense fallback={null}>
                        <SceneContent transformMode={transformMode} />
                        <BuildVolume pb={pb} t={t} gridVisible={gridVisible} showCage={showBuildVolume} />
                    </Suspense>
                    <OrbitControls ref={controlsRef} makeDefault enableDamping dampingFactor={0.08}
                        minDistance={radius * 0.15} maxDistance={radius * 3}
                        maxPolarAngle={Math.PI * 0.495} target={[0, H / 2, 0]} />
                </Canvas>
            </CanvasBoundary>
            <Toolbar transformMode={transformMode} setTransformMode={setTransformMode} />
        </div>
    );
}

/* ===== Full-stack viewport toolbar: selection, transform, scene & quick-add ===== */
function Toolbar({ transformMode, setTransformMode }) {
    const t = useThemeTokens();
    const canUndo = useCadStore((s) => s.history.length > 0);
    const canRedo = useCadStore((s) => s.future.length > 0);
    const hasSelection = useCadStore((s) => !!s.selectedPartId);
    const snap = useCadStore((s) => s.settings.snap);
    const gridVisible = useCadStore((s) => s.settings.gridVisible);
    const updateSetting = useCadStore((s) => s.updateSetting);
    const undo = useCadStore((s) => s.undo);
    const redo = useCadStore((s) => s.redo);

    const btn = (disabled) => ({
        ...TOOL_MODE_STYLE, flexDirection: 'column', padding: '6px 8px', minWidth: 52,
        background: 'transparent', color: t.textSub, opacity: disabled ? 0.35 : 1, cursor: disabled ? 'default' : 'pointer',
    });
    const seg = { width: 1, height: 26, background: t.borderLight, flexShrink: 0, margin: '0 3px' };
    const toolBtn = (id, Icon, label, active) => (
        <button key={id} onClick={() => setTransformMode(id)} title={label}
            style={{ ...TOOL_MODE_STYLE, flexDirection: 'column', padding: '6px 8px', minWidth: 52,
                background: active ? t.accent : 'transparent', color: active ? '#fff' : t.textSub }}>
            <Icon size={16} /><span style={{ fontSize: 10, fontWeight: 700 }}>{label}</span>
        </button>
    );
    return (
        <div style={{
            position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
            gap: 2, padding: 6, maxWidth: '96%',
            background: t.surface, border: `1px solid ${t.borderLight}`,
            borderRadius: 16, boxShadow: t.shadow, zIndex: 10,
        }}>
            {toolBtn('select', MousePointer2, 'Select', transformMode === 'select')}
            {toolBtn('translate', Move, 'Move', transformMode === 'translate')}
            {toolBtn('rotate', RotateCcw, 'Rotate', transformMode === 'rotate')}
            {toolBtn('scale', Maximize, 'Scale', transformMode === 'scale')}
            <div style={seg} />
            <button title={snap ? 'Snap: ON' : 'Snap: OFF'} onClick={() => updateSetting('snap', !snap)}
                style={{ ...btn(false), background: snap ? t.accentSoft : 'transparent', color: snap ? t.accent : t.textSub }}>
                <Magnet size={16} /><span style={{ fontSize: 10, fontWeight: 700 }}>Snap</span>
            </button>
            <button title={gridVisible ? 'Hide Grid' : 'Show Grid'} onClick={() => updateSetting('gridVisible', !gridVisible)}
                style={{ ...btn(false), background: gridVisible ? t.accentSoft : 'transparent', color: gridVisible ? t.accent : t.textSub }}>
                <Grid3x3 size={16} /><span style={{ fontSize: 10, fontWeight: 700 }}>Grid</span>
            </button>
            <div style={seg} />
            <button onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" style={btn(!canUndo)}><Undo2 size={16} /></button>
            <button onClick={redo} disabled={!canRedo} title="Redo" style={btn(!canRedo)}><Redo2 size={16} /></button>
            <button onClick={duplicateSelected} disabled={!hasSelection} title="Duplicate (Ctrl+D)" style={btn(!hasSelection)}><Copy size={16} /></button>
            <button onClick={deleteSelected} disabled={!hasSelection} title="Delete (Del)" style={{ ...btn(!hasSelection), color: t.danger }}><Trash2 size={16} /></button>
            <div style={seg} />
            <button onClick={clearAll} title="Clear build plate" style={btn(false)}><Layers size={16} /></button>
            <button onClick={() => window.__roocad_fitView?.()} title="Fit view to build volume" style={btn(false)}><Focus size={16} /></button>
            <div style={seg} />
            <button onClick={() => addPrimitive('cube')} title="Add Cube" style={btn(false)}><Box size={16} /></button>
            <button onClick={() => addPrimitive('sphere')} title="Add Sphere" style={btn(false)}><Circle size={16} /></button>
            <button onClick={() => addPrimitive('cylinder')} title="Add Cylinder" style={btn(false)}><Cylinder size={16} /></button>
            <button onClick={() => addPrimitive('cone')} title="Add Cone" style={btn(false)}><Crosshair size={16} /></button>
        </div>
    );
}

/* ===== SHARED ACTIONS ===== */
export function addPrimitive(type) {
    const part = { ...geometryEngine.addPrimitive(type, [0, 0.5, 0], [1, 1, 1]), id: Math.random().toString(36).slice(2, 11) };
    const st = useCadStore.getState();
    st.setCurrentModel([...st.currentModel, part]);
    st.setSelectedPart(part.id);
    recordMutation(st.activeProjectId, 'add_shape', { type, part });
}

export function duplicateSelected() {
    const st = useCadStore.getState();
    const part = st.currentModel.find((p) => p.id === st.selectedPartId);
    if (!part) return;
    const copy = { ...part, id: Math.random().toString(36).slice(2, 11), position: [part.position[0] + 1, part.position[1], part.position[2] + 1] };
    st.setCurrentModel([...st.currentModel, copy]);
    st.setSelectedPart(copy.id);
    recordMutation(st.activeProjectId, 'duplicate', { type: copy.type, id: copy.id });
}

export function deleteSelected() {
    const st = useCadStore.getState();
    if (st.selectedPartId) { recordMutation(st.activeProjectId, 'delete', { id: st.selectedPartId }); st.removePart(st.selectedPartId); st.setSelectedPart(null); }
}

export function clearAll() {
    const st = useCadStore.getState();
    if (!st.currentModel.length) return;
    if (!window.confirm('Clear the entire build plate? This cannot be undone.')) return;
    st.setCurrentModel([]);
    st.setSelectedPart(null);
}

export function saveSelectedShape() {
    const st = useCadStore.getState();
    if (!st.currentModel.length) return alert('Nothing to save — add shapes first.');
    const name = window.prompt('Name this shape:', 'My Shape');
    if (name) { st.saveShape(name); alert(`Saved "${name}" to your shape library.`); }
}

/* ===== RIGHT-CLICK CONTEXT MENU ===== */
function ContextMenu({ x, y, onClose }) {
    const t = useThemeTokens();
    const items = [
        { label: 'Duplicate', icon: Copy, fn: duplicateSelected, disabled: !useCadStore.getState().selectedPartId },
        { label: 'Delete', icon: Trash2, fn: deleteSelected, danger: true, disabled: !useCadStore.getState().selectedPartId },
        { label: 'Add Cube', icon: Box, fn: () => addPrimitive('cube') },
        { label: 'Add Sphere', icon: Circle, fn: () => addPrimitive('sphere') },
        { label: 'Add Cylinder', icon: Cylinder, fn: () => addPrimitive('cylinder') },
    ];
    return (
        <div style={{
            position: 'fixed', left: Math.min(x, window.innerWidth - 190), top: Math.min(y, window.innerHeight - 220),
            zIndex: 8000, minWidth: 180, padding: 6, background: t.surface, border: `1px solid ${t.borderLight}`,
            borderRadius: 12, boxShadow: t.shadow, fontFamily: "'Inter', system-ui, sans-serif",
        }} onMouseLeave={onClose}>
            {items.map(({ label, icon: Icon, fn, danger, disabled }) => (
                <button key={label} disabled={disabled} onClick={() => { fn(); onClose(); }} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 10px',
                    border: 'none', borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
                    background: 'transparent', color: disabled ? t.textMuted : (danger ? t.danger : t.text),
                    fontSize: 12.5, fontWeight: 600, textAlign: 'left', opacity: disabled ? 0.5 : 1,
                }}>
                    <Icon size={14} /> {label}
                </button>
            ))}
        </div>
    );
}
/* ===== SHARED PANELS ===== */
const TOOL_MODE_STYLE = {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
    borderRadius: 10, cursor: 'pointer', border: 'none', fontSize: 12,
    fontWeight: 600, transition: 'all 0.15s',
};
function Section({ label, children }) {
    const t = useThemeTokens();
    return (
        <div>
            <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</span>
            {children}
        </div>
    );
}

function ProjectsPanel() {
    const t = useThemeTokens();
    const projects = useCadStore((s) => s.projects);
    const activeProjectId = useCadStore((s) => s.activeProjectId);
    const newProject = useCadStore((s) => s.newProject);
    const switchProject = useCadStore((s) => s.switchProject);
    const deleteProject = useCadStore((s) => s.deleteProject);
    return (
        <Section label={`Projects (${projects.length})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={() => { const n = window.prompt('New project name:', `Project ${projects.length + 1}`); if (n) newProject(n); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: t.accent, border: 'none', borderRadius: 9, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    <FilePlus size={13} /> New Project
                </button>
                {projects.map((p) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button onClick={() => switchProject(p.id)} style={{
                            flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', textAlign: 'left',
                            background: p.id === activeProjectId ? t.accentSoft : t.surface,
                            border: `1px solid ${p.id === activeProjectId ? t.accent : t.borderLight}`, borderRadius: 9,
                            color: t.text, cursor: 'pointer', fontSize: 12, fontWeight: p.id === activeProjectId ? 700 : 500,
                        }}><FolderOpen size={13} color={p.id === activeProjectId ? t.accent : t.textMuted} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                            <span style={{ marginLeft: 'auto', fontSize: 10, color: t.textMuted }}>{p.model?.length || 0}</span>
                        </button>
                        {projects.length > 1 && (
                            <button onClick={() => { if (window.confirm(`Delete project "${p.name}"?`)) deleteProject(p.id); }}
                                style={{ padding: 6, background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 8, color: t.danger, cursor: 'pointer', display: 'flex' }}>
                                <Trash2 size={12} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </Section>
    );
}

function ShapeLibrary({ addPrimitive }) {
    const t = useThemeTokens();
    const savedShapes = useCadStore((s) => s.savedShapes);
    const loadShape = useCadStore((s) => s.loadShape);
    const deleteShape = useCadStore((s) => s.deleteShape);
    const SHAPES = [
        { type: 'cube', label: 'Cube', icon: Box },
        { type: 'sphere', label: 'Sphere', icon: Circle },
        { type: 'cylinder', label: 'Cylinder', icon: Cylinder },
        { type: 'cone', label: 'Cone', icon: Crosshair },
        { type: 'pyramid', label: 'Pyramid', icon: Triangle },
        { type: 'wedge', label: 'Wedge', icon: Spline },
        { type: 'prism', label: 'Prism', icon: Hexagon },
        { type: 'tube', label: 'Tube', icon: CircleDot },
        { type: 'torus', label: 'Torus', icon: Disc },
        { type: 'capsule', label: 'Capsule', icon: Pill },
        { type: 'diamond', label: 'Diamond', icon: Diamond },
        { type: 'gem', label: 'Gem', icon: Gem },
    ];
    return (
        <>
            <Section label="Shape Library">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    {SHAPES.map(({ type, label, icon: Icon }) => (
                        <button key={type} onClick={() => addPrimitive(type)} title={`Add ${label}`} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                            padding: '10px 2px', background: t.surface, border: `1px solid ${t.borderLight}`,
                            borderRadius: 10, color: t.textSub, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                        }}>
                            <Icon size={16} />{label}
                        </button>
                    ))}
                </div>
            </Section>
            {savedShapes.length > 0 && (
                <Section label={`My Shapes (${savedShapes.length})`}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {savedShapes.map((sh) => (
                            <div key={sh.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button onClick={() => loadShape(sh.id)} style={{
                                    flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', textAlign: 'left',
                                    background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 9,
                                    color: t.text, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                }}><Layers size={13} color={t.accent} /> {sh.name}
                                    <span style={{ marginLeft: 'auto', fontSize: 10, color: t.textMuted }}>{sh.parts.length}p</span>
                                </button>
                                <button onClick={() => deleteShape(sh.id)} title="Delete shape" style={{
                                    padding: 7, background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 8,
                                    color: t.danger, cursor: 'pointer', display: 'flex',
                                }}><Trash2 size={13} /></button>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </>
    );
}

function PartInspector() {
    const t = useThemeTokens();
    const selectedPartId = useCadStore((s) => s.selectedPartId);
    const currentModel = useCadStore((s) => s.currentModel);
    const settings = useCadStore((s) => s.settings);
    if (!selectedPartId) return null;
    const part = currentModel.find((p) => p.id === selectedPartId);
    if (!part) return null;
    const numStyle = { flex: 1, padding: '6px 8px', background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 6, color: t.text, fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box' };
    const u = settings.units;
    const dims = ['X', 'Y', 'Z'].map((_, i) => ((part.scale?.[i] ?? 1) * (part.type === 'sphere' ? 1 : 1)));
    const vecRow = (label, arr, onChange, step = 0.1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, color: t.textMuted, width: 34, fontWeight: 700 }}>{label}</span>
            {arr.map((v, i) => (
                <input key={i} type="number" value={v} step={step}
                    onChange={(e) => { const next = [...arr]; next[i] = parseFloat(e.target.value) || 0; onChange(next); }}
                    style={numStyle} />
            ))}
        </div>
    );
    const actBtn = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: 7, background: t.surface, border: `1px solid ${t.borderLight}`, color: t.text, borderRadius: 8, cursor: 'pointer', fontSize: 10.5, fontWeight: 600 };
    return (
        <Section label={`Selected: ${part.type}${part.group ? ' (grouped)' : ''}${part.hidden ? ' (hidden)' : ''}`}>
            <div style={{ background: t.accentSoft, border: `1px solid ${t.borderLight}`, borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {vecRow('Position', part.position, (pos) => useCadStore.getState().updatePart(part.id, { position: pos }))}
                {vecRow('Size', part.scale || [1, 1, 1], (scale) => useCadStore.getState().updatePart(part.id, { scale }))}
                {vecRow('Rot°', part.rotation || [0, 0, 0], (rotation) => useCadStore.getState().updatePart(part.id, { rotation }))}
                <div style={{ fontSize: 10, color: t.textMuted }}>
                    Bounding box: {(part.scale?.[0] ?? 1).toFixed(1)} × {(part.scale?.[1] ?? 1).toFixed(1)} × {(part.scale?.[2] ?? 1).toFixed(1)} {u}
                    {settings.printBed && ` · Bed: ${settings.printBed.x}×${settings.printBed.y}×${settings.printBed.z} ${u}`}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={duplicateSelected} style={actBtn}><Copy size={12} /> Dup</button>
                    <button onClick={() => useCadStore.getState().copySelected()} style={actBtn}><Clipboard size={12} /> Copy</button>
                    <button onClick={() => useCadStore.getState().paste()} style={actBtn}><ClipboardPaste size={12} /> Paste</button>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => useCadStore.getState().toggleVisibility(part.id)} style={actBtn}>
                        {part.hidden ? <Eye size={12} /> : <EyeOff size={12} />} {part.hidden ? 'Show' : 'Hide'}
                    </button>
                    <button onClick={() => (part.group ? useCadStore.getState().ungroupSelected() : useCadStore.getState().groupSelected())} style={actBtn}>
                        <Group size={12} /> {part.group ? 'Ungrp' : 'Group'}
                    </button>
                    <button onClick={() => saveSelectedShape()} style={actBtn}><Save size={12} /> Save</button>
                </div>
                <button onClick={deleteSelected} style={{ ...actBtn, background: t.dangerSoft, border: `1px solid ${t.danger}`, color: t.danger }}>
                    <Trash2 size={12} /> Delete
                </button>
            </div>
        </Section>
    );
}
function AIPanel({ compact }) {
    const t = useThemeTokens();
    const prompt = useCadStore((s) => s.prompt);
    const setPrompt = useCadStore((s) => s.setPrompt);
    const isResearching = useCadStore((s) => s.isResearching);
    const aiStatus = useCadStore((s) => s.aiStatus);
    const modelCount = useCadStore((s) => s.currentModel.length);
    const [lastPrompt, setLastPrompt] = useState('');
    const [correction, setCorrection] = useState('');
    const [teaching, setTeaching] = useState(false);
    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        const st = useCadStore.getState();
        st.setResearching(true); st.setAiStatus('researching');
        setLastPrompt(prompt);
        try {
            const context = await amplify(prompt, st.activeProjectId);   // Vectorize boost
            const geometry = await geometryEngine.generateFromPrompt(prompt, context);
            if (!Array.isArray(geometry) || geometry.length === 0) {
                throw new Error('Nothing to build — the AI returned an empty result.');
            }
            // center on the build plate + fit inside the build volume so nothing is lost
            const pb = st.settings.printBed || { x: 220, y: 220, z: 250 };
            const centered = geometryEngine.centerAndFit(geometry, pb);
            const parts = centered.map((p) => ({ ...p, id: Math.random().toString(36).slice(2, 11), color: p.color || '#3b82f6' }));
            st.setCurrentModel(parts); st.setSelectedPart(null); st.setAiStatus('completed');
            window.__roocad_fitView?.();
        } catch (err) { console.error('Generation failed:', err); useCadStore.getState().setAiStatus('error'); }
        finally { useCadStore.getState().setResearching(false); }
    };
    const handleCorrection = () => {
        if (!correction.trim() || !lastPrompt) return;
        useCadStore.getState().logFeedback(lastPrompt, correction.trim());
        setCorrection(''); setTeaching(false); setPrompt(lastPrompt);
        useCadStore.getState().setAiStatus('learned');
    };
    return (
        <Section label="AI Architect">
            <div style={{ background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the part to replicate…"
                    style={{ width: '100%', boxSizing: 'border-box', minHeight: compact ? 64 : 90, resize: 'vertical', padding: 10, background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 10, color: t.text, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                <button onClick={handleGenerate} disabled={isResearching || !prompt.trim()}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px', background: 'linear-gradient(135deg,#2563eb,#3b82f6)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 13, opacity: isResearching || !prompt.trim() ? 0.5 : 1, cursor: isResearching ? 'wait' : 'pointer' }}>
                    {isResearching ? <Cpu size={16} /> : <Search size={16} />}
                    {isResearching ? 'Researching…' : 'Build Replica'}
                </button>
                {aiStatus === 'error' && <span style={{ fontSize: 11, color: t.danger, fontWeight: 600 }}>Generation failed — check API keys in Settings</span>}
                {aiStatus === 'completed' && <span style={{ fontSize: 11, color: t.success, fontWeight: 600 }}>Model generated successfully — {modelCount} part{modelCount === 1 ? '' : 's'} in the scene</span>}
                {aiStatus === 'completed' && modelCount === 1 && (
                    <span style={{ fontSize: 11, color: t.warning, fontWeight: 600 }}>
                        ⚠ Only a single primitive was produced. For a more faithful replica, ask for more detail
                        (e.g. "table with 4 legs and a thin top") or switch to a stronger provider in Settings.
                    </span>
                )}
                {aiStatus === 'learned' && <span style={{ fontSize: 11, color: t.success, fontWeight: 600 }}>✔ Correction learned — RooCAD will apply it to future generations</span>}
                {aiStatus === 'completed' && (
                    teaching ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <textarea value={correction} onChange={(e) => setCorrection(e.target.value)} placeholder="Teach the AI: what should it do differently next time? (e.g. 'always make panels 3mm thick')"
                                style={{ width: '100%', boxSizing: 'border-box', minHeight: 50, resize: 'vertical', padding: 8, background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 8, color: t.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={handleCorrection} disabled={!correction.trim()} style={{ flex: 1, padding: 8, background: t.accent, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: correction.trim() ? 1 : 0.5 }}>
                                    <MessageSquare size={12} /> Save Lesson
                                </button>
                                <button onClick={() => setTeaching(false)} style={{ padding: 8, background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 8, color: t.textSub, cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setTeaching(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 7, background: 'transparent', border: `1px dashed ${t.borderLight}`, borderRadius: 8, color: t.textMuted, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                            <MessageSquare size={12} /> Not right? Teach the AI a correction
                        </button>
                    )
                )}
            </div>
        </Section>
    );
}

function TopBar({ onOpenSettings }) {
    const t = useThemeTokens();
    const modelCount = useCadStore((s) => s.currentModel.length);
    const history = useCadStore((s) => s.history);
    const undo = useCadStore((s) => s.undo);
    const redo = useCadStore((s) => s.redo);
    const printBed = useCadStore((s) => s.settings.printBed) || { x: 220, y: 220, z: 250 };
    const units = useCadStore((s) => s.settings.units);
    const topBtn = { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700, background: 'transparent', border: `1px solid ${t.borderLight}`, color: t.text };
    return (
        <div style={{ height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', borderBottom: `1px solid ${t.border}`, background: t.bg, gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={undo} disabled={!history.length} title="Undo (Ctrl+Z)" style={{ ...topBtn, opacity: history.length ? 1 : 0.4 }}><Undo2 size={15} /></button>
                <button onClick={redo} disabled={!history.length} title="Redo" style={{ ...topBtn, opacity: history.length ? 1 : 0.4 }}><Redo2 size={15} /></button>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: t.textMuted, marginLeft: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.success, display: 'inline-block' }} />
                    {modelCount} object{modelCount === 1 ? '' : 's'}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span title="Max print dimensions" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.textMuted, padding: '6px 10px', background: t.surface, borderRadius: 8, border: `1px solid ${t.borderLight}` }}>
                    <Printer size={13} /> {printBed.x}×{printBed.y}×{printBed.z} {units}
                </span>
                <button onClick={onOpenSettings} title="Settings" style={topBtn}><Settings size={15} /></button>
                <button onClick={async () => {
                    const group = window.__roocad_scene;
                    if (!group || modelCount === 0) return alert('Nothing to export — add a shape first.');
                    try { await slicerService.exportToSTL(group); alert('STL exported successfully!'); }
                    catch (err) { console.error(err); alert('STL export failed. See console.'); }
                }} style={{ ...topBtn, background: t.accent, border: 'none', color: '#fff' }}><Download size={15} /> Export STL</button>
            </div>
        </div>
    );
}
/* ===== DESKTOP UI ===== */
function DesktopApp({ onOpenSettings }) {
    const t = useThemeTokens();
    const mode = useCadStore((s) => s.mode);
    const setMode = useCadStore((s) => s.setMode);
    const [ctx, setCtx] = useState(null);
    const [showCmd, setShowCmd] = useState(true);
    return (
        <div style={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden' }}>
            {/* LEFT — projects / AI / inspector */}
            <aside style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', background: t.bg, borderRight: `1px solid ${t.border}`, zIndex: 30 }}>
                <div style={{ padding: 14, borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#2563eb,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box size={17} color="#fff" />
                        </div>
                        <div>
                            <h1 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: 0 }}>RooCAD</h1>
                            <span style={{ fontSize: 9.5, color: t.textMuted, fontWeight: 600, letterSpacing: '0.08em' }}>SCRAPE-TO-CAD</span>
                        </div>
                    </div>
                    <button onClick={onOpenSettings} title="Settings" style={{ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, borderRadius: 8, display: 'flex' }}><Settings size={18} /></button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Section label="Mode">
                        <div style={{ display: 'flex', background: t.surface, borderRadius: 10, padding: 4, gap: 4 }}>
                            {['simple', 'advanced'].map((m) => (
                                <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: '7px 4px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, background: mode === m ? t.accent : 'transparent', color: mode === m ? '#fff' : t.textMuted }}>
                                    {m === 'simple' ? 'Simple' : 'Advanced'}
                                </button>
                            ))}
                        </div>
                    </Section>
                    <ProjectsPanel />
                    <AIPanel />
                    <PartInspector />
                </div>
            </aside>
            <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {/* TOP — Boolean toolbar + AI command bar (Tinkercad-style strip) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: `1px solid ${t.border}`, background: t.bg }} >
                    <BooleanToolbar />
                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.textMuted, fontWeight: 600 }}>
                        <button onClick={() => setShowCmd(!showCmd)} style={smallChip(t)}>{showCmd ? 'Hide' : 'Show'} Commands</button>
                        <button onClick={() => setShowCmd(true)} style={smallChip(t)}><Search size={12} /> AI Build</button>
                    </span>
                </div>
                {/* CENTRAL — bounded snap-grid viewport with dynamic LOD */}
                <div style={{ flex: 1, minHeight: 0, position: 'relative', background: t.viewportBg }}>
                    {showCmd && <AICommandBar onClose={() => setShowCmd(false)} />}
                    <CADViewer onContextMenu={(e) => { if (!useCadStore.getState().settings.rightClickMenu) return; e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }} />
                </div>
            </main>
            {/* RIGHT — Tinkercad-style shape library */}
            <aside style={{ width: 216, flexShrink: 0, background: t.bg, borderLeft: `1px solid ${t.border}`, overflowY: 'auto', padding: 14, zIndex: 30 }}>
                <ShapeLibraryPanel onAdd={addPrimitive} />
            </aside>
            {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} />}
        </div>
    );
}
const smallChip = (t) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: t.surface, border: `1px solid ${t.borderLight}`, color: t.textSub, fontWeight: 600, fontSize: 11 });
/* ===== TABLET UI — collapsible drawer ===== */
function TabletApp({ onOpenSettings }) {
    const t = useThemeTokens();
    const [drawer, setDrawer] = useState(true);
    const [ctx, setCtx] = useState(null);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            <TopBar onOpenSettings={onOpenSettings} />
            <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
                {drawer && (
                    <aside style={{ width: 290, flexShrink: 0, background: t.bg, borderRight: `1px solid ${t.border}`, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <ProjectsPanel />
                        <AIPanel compact />
                        <ShapeLibrary addPrimitive={addPrimitive} />
                        <PartInspector />
                    </aside>
                )}
<AICommandBar />
                    <div style={{ position: 'absolute', top: 12, right: 8, zIndex: 22 }}>
                        <BooleanToolbar />
                    </div>
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    <CADViewer onContextMenu={(e) => { if (!useCadStore.getState().settings.rightClickMenu) return; e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }} />
                    <button onClick={() => setDrawer(!drawer)} style={{
                        position: 'absolute', top: 12, left: drawer ? 302 : 12, zIndex: 20, padding: 10,
                        background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 10, cursor: 'pointer',
                        color: t.text, boxShadow: t.shadow, display: 'flex',
                    }}><Layers size={16} /></button>
                </div>
            </div>
            {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} />}
        </div>
    );
}
/* ===== MOBILE UI — bottom toolbar + sheet ===== */
function MobileApp({ onOpenSettings }) {
    const t = useThemeTokens();
    const [sheet, setSheet] = useState(false);
    const SHAPES = [
        { type: 'cube', icon: Box }, { type: 'sphere', icon: Circle },
        { type: 'cylinder', icon: Cylinder }, { type: 'cone', icon: Crosshair },
    ];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            <div style={{ height: 50, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', borderBottom: `1px solid ${t.border}`, background: t.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#2563eb,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box size={15} color="#fff" />
                    </div>
                    <strong style={{ fontSize: 14, color: t.text }}>RooCAD</strong>
                </div>
                <button onClick={onOpenSettings} style={{ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, display: 'flex' }}><Settings size={18} /></button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <CADViewer onContextMenu={(e) => e.preventDefault()} />
            </div>
            <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', gap: 8, padding: '10px 12px', background: t.bg, borderTop: `1px solid ${t.border}` }}>
                {SHAPES.map(({ type, icon: Icon }) => (
                    <button key={type} onClick={() => addPrimitive(type)} style={{
                        width: 46, height: 46, borderRadius: 12, background: t.surface, border: `1px solid ${t.borderLight}`,
                        color: t.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><Icon size={20} /></button>
                ))}
                <button onClick={() => setSheet(true)} style={{
                    flex: 1, height: 46, borderRadius: 12, background: t.accent, border: 'none',
                    color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 700,
                }}><Search size={16} /> AI Build</button>
            </div>
            {sheet && (
                <div onClick={() => setSheet(false)} style={{ position: 'fixed', inset: 0, zIndex: 7000, background: t.overlay, display: 'flex', alignItems: 'flex-end' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: '100%', maxHeight: '72vh', overflowY: 'auto', background: t.bg,
                        borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 20,
                        display: 'flex', flexDirection: 'column', gap: 16, boxShadow: t.shadow,
                    }}>
                        <div style={{ width: 40, height: 4, borderRadius: 4, background: t.borderLight, alignSelf: 'center' }} />
                        <ProjectsPanel />
                        <AIPanel compact />
                        <ShapeLibrary addPrimitive={addPrimitive} />
                        <PartInspector />
                    </div>
                </div>
            )}
        </div>
    );
}
/* ===== ROOT APP ===== */
export default function RooCADApp() {
    const [user, setUser] = useState(() => getSession()?.email || null);
    const [appMode, setAppMode] = useState('3d'); // '3d' | 'sketch'
    const [showSettings, setShowSettings] = useState(false);
    const device = useDeviceType();
    const t = useThemeTokens();

    useEffect(() => {
        const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone;
        useCadStore.getState().setStandalone(!!standalone);
        const capture = (e) => { e.preventDefault(); window.__roocad_install_prompt = e; window.dispatchEvent(new Event('roocad-install-available')); };
        window.addEventListener('beforeinstallprompt', capture);
        const installed = () => useCadStore.getState().setStandalone(true);
        window.addEventListener('appinstalled', installed);
        return () => { window.removeEventListener('beforeinstallprompt', capture); window.removeEventListener('appinstalled', installed); };
    }, []);

    /* installed-app behavior: strip web defaults, full right-click + keyboard control */
    useEffect(() => {
        const { isStandalone, settings } = useCadStore.getState();
        if (!isStandalone || !settings.standaloneBehavior) return;
        const noSelect = document.createElement('style');
        noSelect.textContent = '*{-webkit-user-select:none!important;user-select:none!important;-webkit-touch-callout:none!important}';
        document.head.appendChild(noSelect);
        const noCtx = (e) => { if (!e.target.closest?.('[data-roocad-menu]')) e.preventDefault(); };
        document.addEventListener('contextmenu', noCtx);
        return () => { noSelect.remove(); document.removeEventListener('contextmenu', noCtx); };
    }, []);

    /* global keyboard shortcuts */
    useEffect(() => {
        const fn = (e) => {
            if (!useCadStore.getState().settings.keyboardShortcuts) return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
            if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected(); }
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateSelected(); }
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? useCadStore.getState().redo() : useCadStore.getState().undo(); }
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { e.preventDefault(); useCadStore.getState().copySelected(); }
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') { e.preventDefault(); useCadStore.getState().paste(); }
            else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') { e.preventDefault(); useCadStore.getState().groupSelected(); }
            else if (e.key.toLowerCase() === 'h' && useCadStore.getState().selectedPartId) { useCadStore.getState().toggleVisibility(useCadStore.getState().selectedPartId); }
            else if (e.key === 'Escape') useCadStore.getState().setSelectedPart(null);
        };
        window.addEventListener('keydown', fn);
        return () => window.removeEventListener('keydown', fn);
    }, []);

    if (!user) return <LoginScreen onLogin={(email) => setUser(email)} />;

    if (appMode === 'sketch') {
        return (
            <div style={{ height: '100vh', width: '100vw', overflow: 'hidden', background: t.bg, color: t.text, fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
                <Sketch2D onExit={() => setAppMode('3d')} onOpenSettings={() => setShowSettings(true)} email={user} onLogout={() => { logout(); setUser(null); }} />
                {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
            </div>
        );
    }

    return (
        <div style={{ height: '100vh', width: '100vw', overflow: 'hidden', background: t.bg, color: t.text, fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
            {/* App-mode switcher rail */}
            <div style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 500, display: 'flex', gap: 2, background: t.bgSub, border: `1px solid ${t.borderLight}`, borderRadius: 12, padding: 4, boxShadow: t.shadow }}>
                {[['3d', '3D Model', Box], ['sketch', '2D Sketch', Spline]].map(([id, label, Icon]) => (
                    <button key={id} onClick={() => setAppMode(id)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, background: appMode === id ? '#2563eb' : 'transparent', color: appMode === id ? '#fff' : t.textMuted,
                    }}><Icon size={13} />{label}</button>
                ))}
                <span style={{ width: 1, background: t.borderLight, margin: '4px 6px' }} />
                <button onClick={() => setShowSettings(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: 'transparent', color: t.textMuted }}><Settings size={13} />Settings</button>
                <button onClick={() => { logout(); setUser(null); }} title={user} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: 'transparent', color: t.textMuted }}><LogOut size={13} /></button>
            </div>
            {device === 'mobile' ? <MobileApp onOpenSettings={() => setShowSettings(true)} />
                : device === 'tablet' ? <TabletApp onOpenSettings={() => setShowSettings(true)} />
                    : <DesktopApp onOpenSettings={() => setShowSettings(true)} />}
            {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
        </div>
    );
}
